/**
 * Firestore-backed RTK Query API for the building list.
 *
 * Replaces the REST polling in BuildingSelector/Preferences/PreferenceEditorSlideIn
 * (useGetAllBuildingsQuery) with a live onSnapshot listener on the `buildings` collection.
 * BuildingAdmin.tsx deliberately keeps using the REST endpoint (it also mutates via
 * delete/set-default, so splitting its read/write paths would buy nothing).
 *
 * Firestore path (Kotlin server side): buildings/{name} (BuildingRepository.kt).
 * As of specs/016-direct-firestore-reads, the stored `rooms` array already includes the
 * synthetic AutomaticAssignment/NoRoom entries (Building.kt/BuildingRepository.kt) -- this
 * mapper does not need to (and must not) re-add them.
 *
 * `name` is the building's real, mutable display name (`raw.name`, e.g. "Hof van Zevenbergen")
 * -- for rendering, and for any REST call whose `building` param is resolved server-side via
 * `BuildingRepository.get()`'s id-or-name lookup (specs/038-building-lookup-by-id). `id` is the
 * Firestore document id (e.g. "hof_van_zevenbergen") -- required wherever a consumer builds a
 * Firestore path directly in the browser (kantoordagDayViewStreamingApi.ts,
 * kantoordagBuildingViewStreamingApi.ts, kantoordagParkingViewStreamingApi.ts), since those paths
 * are written server-side under the id-normalized form (`BuildingName.toId()`), not the display
 * name. Get this wrong and either the UI shows a raw slug instead of a label, or a Firestore
 * listener silently finds nothing.
 */

import { collection, CollectionReference, Firestore, QuerySnapshot } from 'firebase/firestore';
import { BuildingView, Room } from '../kantoordagApi';
import { createFirestoreStreamApi } from '../firestoreModel';

export type FirestoreBuildingView = BuildingView & { id: string };

type RawDoc = Record<string, unknown>;

function mapRoom(raw: RawDoc): Room {
    return {
        name: (raw.name as string) ?? '',
        seats: (raw.seats as number) ?? 0,
    };
}

function mapBuilding(docId: string, raw: RawDoc): FirestoreBuildingView {
    const rooms = ((raw.rooms as RawDoc[]) ?? []).map(mapRoom);

    // Mirrors Building.calculateRooms() -- seats/seatsPerRoom are calculated, never persisted,
    // and exclude the synthetic entries (both have seats: -1).
    const seatsPerRoom: Record<string, number> = {};
    let seats = 0;
    rooms.forEach((room) => {
        if (room.seats >= 0) {
            seats += room.seats;
            seatsPerRoom[room.name] = room.seats;
        }
    });

    return {
        id: docId,
        name: (raw.name as string) ?? docId,
        parkingSpots: (raw.parkingSpots as number) ?? 0,
        parkingGate: (raw.parkingGate as string | null | undefined) ?? null,
        rooms,
        // Mirrors Building.toView(): "$appUrl/building/$picture", where appUrl (WEB_URL) is this
        // app's own origin -- firebase.json rewrites /building/* to the backend on that same origin.
        picture: `${window.location.origin}/building/${(raw.picture as string) ?? ''}`,
        default: (raw.default as boolean) ?? false,
        seats,
        seatsPerRoom,
        features: {
            hasParking: (raw.hasParking as boolean) ?? false,
            hasRooms: (raw.hasRooms as boolean) ?? false,
        },
    };
}

function mapData(snapshot: QuerySnapshot): FirestoreBuildingView[] {
    return snapshot.docs.map((doc) => mapBuilding(doc.id, doc.data() as RawDoc));
}

export const buildingsStreamingApi = createFirestoreStreamApi<FirestoreBuildingView[], void>({
    reducerPath: 'buildingsApi',

    getRef: (db: Firestore): CollectionReference => collection(db, 'buildings'),

    mapData,
    emptyResult: () => [],
});

export const useGetBuildingsQuery = buildingsStreamingApi.endpoints.get.useQuery;
